/**
 * Autocadastro do transportador — a inscrição no credenciamento TEG, ligada à
 * API real (F05). Três momentos, nesta ordem e nunca ao mesmo tempo:
 *
 * 1. **O portão de identidade** (F04), enquanto não há sessão do inscrito.
 *    Quem já entrou nesta aba (sessão aberta) pula direto para o passo 2 — é
 *    assim que se retoma um rascunho (RN-CP-26: entrar e continuar).
 * 2. **A escolha da entidade** (D-13): perfis do SGTP do CPF da sessão, com a
 *    situação de cada um no período aberto. Sem período aberto, a tela explica
 *    o porquê e não abre assistente nenhum (RN-CP-03).
 * 3. **O assistente de quatro etapas**, salvo no servidor a cada avanço:
 *    contatos e veículos → configurar veículos → edital e privacidade →
 *    confirmação e envio. O envio é idempotente (a chave nasce uma vez por
 *    assistente): duplo clique ou rede que caiu devolvem os mesmos protocolos.
 *
 * O CPF completo nunca chega a esta tela: o perfil pessoa física é a chave
 * `PF`, e o servidor sabe o CPF pela sessão.
 */

import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ArrowLeftRight, CalendarX2, Info, ShieldCheck } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { AssistenteDeEtapas, useAssistenteDeEtapas } from '@/ui/AssistenteDeEtapas';
import type { EtapaDoAssistente as EtapaDoComponente } from '@/ui/AssistenteDeEtapas';
import { EmptyState } from '@/ui/EmptyState';
import { Spinner } from '@/ui/Spinner';
import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';

import {
  CHAVES_DA_INSCRICAO,
  apiDaInscricao,
  mensagemDoErro,
  useEstadoDoPeriodo,
  useVeiculosDoPerfil,
  type ContatosDoRascunho,
  type DadosDoRascunho,
  type Pendencia,
  type PerfilDoInscrito,
  type Rascunho,
  type VeiculoDoRascunho,
} from './apiDaInscricao';
import {
  ETAPAS_DO_AUTOCADASTRO,
  TEMPO_ESTIMADO_DO_AUTOCADASTRO,
  formatarCnpj,
  formatarPlaca,
} from './contrato';
import { EscolhaDaEntidade, type EscolhaDaEntidadeProps } from './EscolhaDaEntidade';
import { EtapaAceiteDoEdital } from './EtapaAceiteDoEdital';
import { EtapaConfigurarVeiculos } from './EtapaConfigurarVeiculos';
import { EtapaConfirmacao } from './EtapaConfirmacao';
import { EtapaVeiculosSgtp } from './EtapaVeiculosSgtp';
import { CHAVE_DA_SESSAO_DO_INSCRITO, useSessaoDoInscrito } from './identidade/api';
import { PortaoDeIdentidade } from './identidade/PortaoDeIdentidade';
import type { ViaDaIdentidade } from './identidade/maquinaDeIdentidade';
import { problemasDaEtapa, type ProblemaDaEtapa } from './validacaoDoAssistente';

const ETAPAS: readonly EtapaDoComponente[] = ETAPAS_DO_AUTOCADASTRO.map((etapa) => ({
  id: `autocadastro-etapa-${etapa.numero}`,
  titulo: etapa.rotulo,
  descricao: etapa.descricao,
}));

const ROTULO_DA_VIA: Record<ViaDaIdentidade, string> = {
  GOVBR: 'gov.br',
  EMAIL: 'Código por e-mail',
  SMS: 'Código por SMS',
};

export function AutoCadastroPage() {
  const cliente = useQueryClient();
  const sessao = useSessaoDoInscrito();
  const periodo = useEstadoDoPeriodo();
  const [via, setVia] = useState<ViaDaIdentidade | null>(null);

  const semSessao = !sessao.data && via === null;

  if ((sessao.isPending && via === null) || (semSessao && periodo.isPending)) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-10 sm:px-6">
        <h1 className="sr-only">Inscrição no Credenciamento TEG</h1>
        <Spinner rotulo="Conferindo o período de inscrição" />
      </div>
    );
  }

  // Sem período aberto, não há por que pedir identidade (RN-CP-03). Se a rota
  // pública falhar, o portão segue: a escolha da entidade confere de novo.
  if (semSessao && periodo.data && !periodo.data.aceitaInscricoes) {
    return (
      <div className="mx-auto w-full max-w-3xl space-y-6 px-4 py-10 sm:px-6">
        <h1 className="text-2xl font-bold text-foreground sm:text-3xl">
          Inscrição no Credenciamento TEG
        </h1>
        <EmptyState
          icon={CalendarX2}
          title="Não há período de inscrição aberto agora"
          description={periodo.data.mensagem}
        />
        <p className="text-center text-sm text-muted-foreground">
          Já se inscreveu?{' '}
          <Button asChild variant="link" className="h-auto p-0">
            <Link to="/credenciados/entrar">Entre para acompanhar</Link>
          </Button>
        </p>
      </div>
    );
  }

  // Sem sessão (ou sem como saber): o portão. Quem acabou de passar por ele
  // tem `via`; quem voltou com a sessão aberta tem `sessao.data`.
  if (semSessao) {
    return (
      <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6 sm:py-12">
        <PortaoDeIdentidade
          aoConfirmar={(identidade) => {
            setVia(identidade.via);
            void cliente.invalidateQueries({ queryKey: CHAVE_DA_SESSAO_DO_INSCRITO });
          }}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6 px-4 py-8 sm:px-6">
      <header className="space-y-2">
        <h1 className="text-2xl font-bold text-foreground sm:text-3xl">
          Inscrição no Credenciamento TEG
        </h1>
        <p className="text-sm text-muted-foreground">
          Para quem já tem cadastro no SGTP. Você escolhe a entidade, os veículos e as escolas de
          cada um, e aceita o edital. Cada veículo recebe o seu protocolo.
        </p>
        <Badge variant="outline" className="gap-1.5 border-success/40 bg-success/5 text-success">
          <ShieldCheck aria-hidden="true" className="h-3.5 w-3.5" />
          Identidade verificada
          {via ? ` · ${ROTULO_DA_VIA[via]}` : ''}
          {sessao.data ? ` · CPF ${sessao.data.inscrito.cpfMascarado}` : ''}
        </Badge>
      </header>

      <InscricaoDoInscrito />
    </div>
  );
}

function InscricaoDoInscrito() {
  const cliente = useQueryClient();
  const [aberta, setAberta] = useState<{ rascunho: Rascunho; perfil: PerfilDoInscrito } | null>(
    null,
  );
  const [abrindo, setAbrindo] = useState<string | null>(null);
  const [aviso, setAviso] = useState<EscolhaDaEntidadeProps['aviso']>(null);

  async function escolher(perfil: PerfilDoInscrito) {
    setAbrindo(perfil.chave);
    setAviso(null);
    try {
      const r = await apiDaInscricao.iniciar(perfil.chave);
      if (r.resultado === 'INSCRICAO_JA_ENVIADA') {
        setAviso({
          titulo: 'Esta entidade já se inscreveu neste período',
          texto: r.mensagem,
          protocolos: r.protocolos,
        });
        void cliente.invalidateQueries({ queryKey: CHAVES_DA_INSCRICAO.perfis });
      } else if (r.resultado === 'CICLO_FECHADO' || !r.rascunho.ciclo) {
        setAviso({
          titulo: 'O período de inscrição não está aberto',
          texto:
            r.resultado === 'CICLO_FECHADO'
              ? r.mensagem
              : 'O período desta inscrição terminou. Acompanhe a abertura do próximo.',
        });
        void cliente.invalidateQueries({ queryKey: CHAVES_DA_INSCRICAO.perfis });
      } else {
        setAberta({ rascunho: r.rascunho, perfil });
      }
    } catch (erro) {
      setAviso({
        titulo: 'Não foi possível abrir a inscrição',
        texto: mensagemDoErro(erro, 'Tente de novo em alguns minutos.'),
      });
    } finally {
      setAbrindo(null);
    }
  }

  if (!aberta) {
    return (
      <EscolhaDaEntidade aoEscolher={(p) => void escolher(p)} abrindo={abrindo} aviso={aviso} />
    );
  }

  return (
    <AssistenteDaInscricao
      key={aberta.rascunho.id}
      inicial={aberta.rascunho}
      perfil={aberta.perfil}
      aoTrocarDeEntidade={() => {
        setAberta(null);
        void cliente.invalidateQueries({ queryKey: CHAVES_DA_INSCRICAO.perfis });
      }}
    />
  );
}

function novoVeiculo(placa: string): VeiculoDoRascunho {
  return { placa, modalidade: null, dreId: null, escolas: [], cooperativaCnpj: null };
}

function normalizarContatos(contatos: ContatosDoRascunho): ContatosDoRascunho {
  return {
    email: contatos.email?.trim() || null,
    telefone: contatos.telefone?.replace(/\D/g, '') || null,
  };
}

function AssistenteDaInscricao({
  inicial,
  perfil,
  aoTrocarDeEntidade,
}: {
  inicial: Rascunho;
  perfil: PerfilDoInscrito;
  aoTrocarDeEntidade: () => void;
}) {
  const ciclo = inicial.ciclo!;
  const navegar = useNavigate();
  const cliente = useQueryClient();
  const [dados, setDados] = useState<DadosDoRascunho>(inicial.dados);
  const [contatos, setContatos] = useState<ContatosDoRascunho>(inicial.contatos);
  const [versao, setVersao] = useState(inicial.versao);
  // A etapa em que o "Próximo" foi recusado. Os problemas não ficam guardados:
  // são recalculados a cada mudança, e a caixa acompanha o que o inscrito
  // corrige — some quando tudo está certo, volta se ele desfizer.
  const [recusadaNaEtapa, setRecusadaNaEtapa] = useState<number | null>(null);
  const [pendencias, setPendencias] = useState<Pendencia[]>([]);
  const [recusa, setRecusa] = useState<string | null>(null);
  const [retirados, setRetirados] = useState<string[]>([]);
  const chaveDoEnvio = useRef(`inscricao-${crypto.randomUUID()}`);
  const enviando = useRef(false);
  const [enviandoAgora, setEnviandoAgora] = useState(false);

  const { etapaAtual, concluidas, avancar, voltar, irPara, retomar } = useAssistenteDeEtapas(
    ETAPAS.length,
  );
  const veiculos = useVeiculosDoPerfil(perfil.chave);
  const problemas: ProblemaDaEtapa[] =
    recusadaNaEtapa === etapaAtual ? problemasDaEtapa(etapaAtual, { dados, contatos, ciclo }) : [];

  // Retomada: volta à etapa em que o rascunho parou. O servidor só grava a
  // etapa seguinte depois de a anterior passar pela conferência, então as
  // etapas de antes contam como concluídas — com o ✓ e o atalho no indicador.
  const retomou = useRef(false);
  useEffect(() => {
    if (retomou.current) return;
    retomou.current = true;
    if (inicial.etapa > 0) retomar(Math.min(inicial.etapa, ETAPAS.length - 1));
  }, [inicial.etapa, retomar]);

  // Veículo do rascunho que deixou de poder entrar (licença venceu, outra
  // entidade inscreveu a placa) sai da seleção — e a tela diz qual saiu.
  useEffect(() => {
    if (!veiculos.data) return;
    const podem = new Set(veiculos.data.filter((v) => v.selecionavel).map((v) => v.placa));
    setDados((atual) => {
      const fora = atual.veiculos.filter((v) => !podem.has(v.placa)).map((v) => v.placa);
      if (fora.length === 0) return atual;
      setRetirados(fora);
      return { ...atual, veiculos: atual.veiculos.filter((v) => podem.has(v.placa)) };
    });
  }, [veiculos.data]);

  const salvar = useCallback(
    async (etapa: number): Promise<boolean> => {
      try {
        const r = await apiDaInscricao.salvar(inicial.id, {
          versao,
          etapa,
          dados,
          contatos: normalizarContatos(contatos),
        });
        setVersao(r.versao);
        return true;
      } catch (erro) {
        setRecusa(
          mensagemDoErro(
            erro,
            'Não foi possível salvar o que você preencheu. Confira a conexão e tente de novo.',
          ),
        );
        return false;
      }
    },
    [contatos, dados, inicial.id, versao],
  );

  const aoAvancar = useCallback(async () => {
    setRecusa(null);
    const encontrados = problemasDaEtapa(etapaAtual, { dados, contatos, ciclo });
    setRecusadaNaEtapa(encontrados.length > 0 ? etapaAtual : null);
    if (encontrados.length > 0) return false;
    if (!(await salvar(etapaAtual + 1))) return false;
    avancar();
    return true;
  }, [avancar, ciclo, contatos, dados, etapaAtual, salvar]);

  const aoConcluir = useCallback(async () => {
    if (enviando.current) return;
    enviando.current = true;
    setEnviandoAgora(true);
    setRecusa(null);
    setPendencias([]);
    try {
      const r = await apiDaInscricao.enviar(inicial.id, versao, chaveDoEnvio.current);
      if (r.resultado === 'PENDENCIAS') {
        setPendencias(r.pendencias);
        return;
      }
      const primeiro = r.comprovante.veiculos[0]!.protocolo;
      cliente.setQueryData(CHAVES_DA_INSCRICAO.comprovante(primeiro), r.comprovante);
      void cliente.invalidateQueries({ queryKey: CHAVES_DA_INSCRICAO.perfis });
      navegar(`/credenciados/enviada/${primeiro}`);
    } catch (erro) {
      setRecusa(
        mensagemDoErro(
          erro,
          'Não foi possível enviar agora. Nada foi enviado; tente de novo em instantes.',
        ),
      );
    } finally {
      enviando.current = false;
      setEnviandoAgora(false);
    }
  }, [cliente, inicial.id, navegar, versao]);

  function alternarVeiculo(placa: string, marcado: boolean) {
    setDados((atual) => ({
      ...atual,
      veiculos: marcado
        ? [...atual.veiculos.filter((v) => v.placa !== placa), novoVeiculo(placa)]
        : atual.veiculos.filter((v) => v.placa !== placa),
    }));
  }

  function atualizarVeiculo(placa: string, veiculo: VeiculoDoRascunho) {
    setDados((atual) => ({
      ...atual,
      veiculos: atual.veiculos.map((v) => (v.placa === placa ? veiculo : v)),
    }));
  }

  return (
    <AssistenteDeEtapas
      etapas={ETAPAS}
      etapaAtual={etapaAtual}
      concluidas={concluidas}
      aoAvancar={aoAvancar}
      aoVoltar={() => {
        setRecusadaNaEtapa(null);
        voltar();
      }}
      aoIrPara={(indice) => {
        setRecusadaNaEtapa(null);
        irPara(indice);
      }}
      rotuloDaConclusao="Enviar inscrição"
      aoConcluir={() => void aoConcluir()}
      tempoEstimado={TEMPO_ESTIMADO_DO_AUTOCADASTRO}
      rotuloAcessivel="Etapas do autocadastro"
      cabecalho={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-foreground">
            <span className="font-semibold">{inicial.entidade.nome}</span>{' '}
            <span className="font-mono text-xs text-muted-foreground">
              {formatarCnpj(inicial.entidade.documento)}
            </span>
          </p>
          <Button type="button" variant="ghost" size="sm" onClick={aoTrocarDeEntidade}>
            <ArrowLeftRight aria-hidden="true" />
            Trocar de entidade
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {enviandoAgora && (
          <p role="status" className="text-sm text-muted-foreground">
            Enviando a inscrição… não feche esta página.
          </p>
        )}
        {problemas.length > 0 && (
          <Alert variant="destructive">
            <AlertCircle aria-hidden="true" className="h-4 w-4" />
            <AlertTitle>Confira antes de continuar</AlertTitle>
            <AlertDescription>
              <ul className="list-disc space-y-0.5 pl-4">
                {problemas.map((p, i) => (
                  <li key={`${p.placa ?? ''}-${i}`}>
                    {p.placa ? `${formatarPlaca(p.placa)}: ` : ''}
                    {p.mensagem}
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}
        {recusa && (
          <Alert variant="destructive">
            <AlertCircle aria-hidden="true" className="h-4 w-4" />
            <AlertDescription>{recusa}</AlertDescription>
          </Alert>
        )}
        {retirados.length > 0 && etapaAtual === 0 && (
          <Alert>
            <Info aria-hidden="true" className="h-4 w-4" />
            <AlertDescription>
              Saíram da seleção porque não podem mais ser inscritos:{' '}
              {retirados.map(formatarPlaca).join(', ')}.
            </AlertDescription>
          </Alert>
        )}

        {etapaAtual === 0 && (
          <EtapaVeiculosSgtp
            contatos={contatos}
            aoTrocarContatos={setContatos}
            veiculos={veiculos}
            placasMarcadas={dados.veiculos.map((v) => v.placa)}
            aoAlternar={alternarVeiculo}
          />
        )}

        {etapaAtual === 1 && (
          <EtapaConfigurarVeiculos
            ciclo={ciclo}
            veiculos={dados.veiculos}
            doSgtp={veiculos.data ?? []}
            aoAtualizar={atualizarVeiculo}
          />
        )}

        {etapaAtual === 2 && (
          <EtapaAceiteDoEdital
            ciclo={ciclo}
            lgpdVersao={inicial.lgpdVersao}
            aceiteDoEdital={dados.aceiteEdital && dados.editalVersao === ciclo.editalVersao}
            aoAceitarOEdital={(aceite) =>
              setDados((atual) => ({
                ...atual,
                aceiteEdital: aceite,
                editalVersao: aceite ? ciclo.editalVersao : null,
              }))
            }
            consentimentoLgpd={dados.aceiteLgpd}
            aoConsentir={(aceite) => setDados((atual) => ({ ...atual, aceiteLgpd: aceite }))}
          />
        )}

        {etapaAtual === 3 && (
          <EtapaConfirmacao
            ciclo={ciclo}
            entidade={inicial.entidade}
            contatos={normalizarContatos(contatos)}
            dados={dados}
            pendencias={pendencias}
          />
        )}
      </div>
    </AssistenteDeEtapas>
  );
}

export default AutoCadastroPage;
